import { useAppTranslation } from 'app/config/i18Config/hooks';
import dayjs, { Dayjs } from 'dayjs';
import { TableFilter, useTableFilterContext } from 'features/TableFilter';
import { FC, memo, useCallback } from 'react';
import { RangeDatePicker } from 'shared/ui/RangeDatePicker/RangeDatePicker';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { PriceRaiseListFilterKeys } from '../../model/types';
import { PriceRaiseStatusSelect } from './PriceRaiseStatusSelect';
import { WarehouseSelect } from './WarehouseSelect';

interface PriceRaiseListFilterProps {
  showNextReviewDate?: boolean;
  showNotificationSentAt?: boolean;
  statusFilterMode?: 'unprocessed' | 'processed';
}

export const PriceRaiseListFilter: FC<PriceRaiseListFilterProps> = memo((props) => {
  const { showNextReviewDate, showNotificationSentAt, statusFilterMode } = props;

  const { t } = useAppTranslation(['contracts', 'common']);

  const { filters, changeFilters, clearCurrentFilters } = useTableFilterContext<PriceRaiseListFilterKeys>();

  const selectWarehouse = useCallback(
    (value: string[]) => {
      changeFilters({
        key: PriceRaiseListFilterKeys.selectedWarehouses,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Warehouse'),
      });
    },
    [changeFilters, t],
  );

  const changePriceRaiseDateFrom = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: PriceRaiseListFilterKeys.priceRaiseDateFrom,
        label: t('From {{priceRaiseDateFrom}}', { priceRaiseDateFrom: clientFormatDateFrom }),
        value,
        title: t('Price raise date'),
      });
    },
    [changeFilters, t],
  );

  const changePriceRaiseDateTo = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: PriceRaiseListFilterKeys.priceRaiseDateTo,
        label: t('To {{priceRaiseDateTo}}', { priceRaiseDateTo: clientFormatDateTo }),
        value,
        title: t('Price raise date'),
      });
    },
    [changeFilters, t],
  );

  const changeNextReviewDateFrom = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: PriceRaiseListFilterKeys.nextReviewDateFrom,
        label: t('From {{priceRaiseDateFrom}}', { priceRaiseDateFrom: clientFormatDateFrom }),
        value,
        title: t('Review again date'),
      });
    },
    [changeFilters, t],
  );

  const changeNextReviewDateTo = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: PriceRaiseListFilterKeys.nextReviewDateTo,
        label: t('To {{priceRaiseDateTo}}', { priceRaiseDateTo: clientFormatDateTo }),
        value,
        title: t('Review again date'),
      });
    },
    [changeFilters, t],
  );

  const changeNotificationSentAtFrom = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: PriceRaiseListFilterKeys.notificationSentAtFrom,
        label: t('From {{priceRaiseDateFrom}}', { priceRaiseDateFrom: clientFormatDateFrom }),
        value,
        title: t('Notification sent date'),
      });
    },
    [changeFilters, t],
  );

  const changeNotificationSentAtTo = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: PriceRaiseListFilterKeys.notificationSentAtTo,
        label: t('To {{priceRaiseDateTo}}', { priceRaiseDateTo: clientFormatDateTo }),
        value,
        title: t('Notification sent date'),
      });
    },
    [changeFilters, t],
  );

  const selectStatus = useCallback(
    (value: string, label: string) => {
      changeFilters({
        key: PriceRaiseListFilterKeys.status,
        label,
        value,
        title: t('Status'),
      });
    },
    [changeFilters, t],
  );

  return (
    <TableFilter>
      <div className="space-y-2">
        <WarehouseSelect values={filters.selectedWarehouses?.value} onChange={selectWarehouse} onClear={clearCurrentFilters} />
        {statusFilterMode && (
          <PriceRaiseStatusSelect
            value={filters.status?.value}
            mode={statusFilterMode}
            onChange={selectStatus}
            onClear={clearCurrentFilters}
          />
        )}
        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Price raise date')}
          onChangeFrom={changePriceRaiseDateFrom}
          onChangeTo={changePriceRaiseDateTo}
          values={[
            filters.priceRaiseDateFrom?.value && dayjs(filters.priceRaiseDateFrom?.value),
            filters.priceRaiseDateTo?.value && dayjs(filters.priceRaiseDateTo?.value),
          ]}
        />
        {showNextReviewDate && (
          <RangeDatePicker
            placeholder={[t('From'), t('To')]}
            label={t('Review again date')}
            onChangeFrom={changeNextReviewDateFrom}
            onChangeTo={changeNextReviewDateTo}
            values={[
              filters.nextReviewDateFrom?.value && dayjs(filters.nextReviewDateFrom?.value),
              filters.nextReviewDateTo?.value && dayjs(filters.nextReviewDateTo?.value),
            ]}
          />
        )}
        {showNotificationSentAt && (
          <RangeDatePicker
            placeholder={[t('From'), t('To')]}
            label={t('Notification sent date')}
            onChangeFrom={changeNotificationSentAtFrom}
            onChangeTo={changeNotificationSentAtTo}
            values={[
              filters.notificationSentAtFrom?.value && dayjs(filters.notificationSentAtFrom?.value),
              filters.notificationSentAtTo?.value && dayjs(filters.notificationSentAtTo?.value),
            ]}
          />
        )}
      </div>
    </TableFilter>
  );
});
